import { useState, useEffect } from "react";
import axios from "axios";
import { useRBAC, GOVERNANCE_ROLES } from "../../contexts/RBACContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
  Crown,
  Briefcase,
  Scale,
  UserCheck,
  Eye,
  Plus,
  Trash2,
  Users,
  Shield,
  Search,
  AlertCircle,
  CheckCircle,
  X,
  ChevronDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Role configuration
const ROLE_CONFIG = {
  [GOVERNANCE_ROLES.CHAIR]: {
    icon: Crown,
    label: "Chair",
    color: "amber",
    description: "Can sign and approve minutes",
  },
  [GOVERNANCE_ROLES.SECRETARY]: {
    icon: Briefcase,
    label: "Secretary",
    color: "blue",
    description: "Full edit access, can approve but not sign",
  },
  [GOVERNANCE_ROLES.LEGAL]: {
    icon: Scale,
    label: "Legal Counsel",
    color: "purple",
    description: "Read access with legal hold management",
  },
  [GOVERNANCE_ROLES.DIRECTOR]: {
    icon: UserCheck,
    label: "Director",
    color: "emerald",
    description: "View minutes and basic exports",
  },
  [GOVERNANCE_ROLES.OBSERVER]: {
    icon: Eye,
    label: "Observer",
    color: "slate",
    description: "View only access to approved minutes",
  },
};

export default function RoleManagement() {
  const { roleAssignments, fetchRoleAssignments, assignRole, removeRole } = useRBAC();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(GOVERNANCE_ROLES.DIRECTOR);
  const [scopeType, setScopeType] = useState("global");
  const [meetingScope, setMeetingScope] = useState("");
  const [committeeScope, setCommitteeScope] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Fetch entity users
  useEffect(() => {
    fetchUsers();
    fetchRoleAssignments();
  }, [fetchRoleAssignments]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/company/team`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }

    setSaving(true);
    try {
      await assignRole(
        selectedUser.id,
        selectedRole,
        scopeType === "meeting" ? meetingScope : null,
        scopeType === "committee" ? committeeScope : null
      );
      toast.success(`Role assigned to ${selectedUser.name}`);
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign role");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveRole = async (assignmentId, userName) => {
    if (!window.confirm(`Remove role from ${userName}?`)) return;

    try {
      await removeRole(assignmentId);
      toast.success("Role removed");
    } catch (error) {
      toast.error("Failed to remove role");
    }
  };

  const resetForm = () => {
    setSelectedUser(null);
    setSelectedRole(GOVERNANCE_ROLES.DIRECTOR);
    setScopeType("global");
    setMeetingScope("");
    setCommitteeScope("");
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleConfig = (role) => ROLE_CONFIG[role] || ROLE_CONFIG[GOVERNANCE_ROLES.OBSERVER];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary-600" />
            Role Management
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Assign governance roles to control access and permissions
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-primary-600 hover:bg-primary-700">
          <Plus className="w-4 h-4 mr-2" />
          Assign Role
        </Button>
      </div>

      {/* Role Legend */}
      <div className="bg-slate-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-slate-700 mb-3">Available Roles</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(ROLE_CONFIG).map(([role, config]) => {
            const Icon = config.icon;
            return (
              <div key={role} className="flex flex-col items-center text-center p-3 bg-white rounded-lg border border-slate-200">
                <div className={`w-10 h-10 rounded-full bg-${config.color}-50 flex items-center justify-center mb-2`}>
                  <Icon className={`w-5 h-5 text-${config.color}-600`} />
                </div>
                <span className="text-sm font-medium text-slate-800">{config.label}</span>
                <span className="text-xs text-slate-500 mt-1">{config.description}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Role Assignments List */}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-800">Current Role Assignments</h3>
        </div>

        {roleAssignments.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No roles assigned yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Click &ldquo;Assign Role&rdquo; to get started
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {roleAssignments.map((assignment) => {
              const config = getRoleConfig(assignment.governance_role);
              const Icon = config.icon;
              
              return (
                <div key={assignment.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full bg-${config.color}-50 flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 text-${config.color}-600`} />
                    </div>
                    <div>
                      <div className="font-medium text-slate-800">
                        {assignment.user_name || assignment.user_email}
                      </div>
                      <div className="text-sm text-slate-500 flex items-center gap-2">
                        <span className={`text-${config.color}-600 font-medium`}>{config.label}</span>
                        {assignment.meeting_scope && (
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded">
                            Meeting: {assignment.meeting_scope}
                          </span>
                        )}
                        {assignment.committee_scope && (
                          <span className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded">
                            Committee: {assignment.committee_scope}
                          </span>
                        )}
                        {!assignment.meeting_scope && !assignment.committee_scope && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                            Global
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveRole(assignment.id, assignment.user_name)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Role Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Assign Governance Role</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* User Selection */}
              <div>
                <Label className="text-slate-700">Select User</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg">
                  {filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => {
                        setSelectedUser(user);
                        setSearchQuery("");
                      }}
                      className={`w-full p-3 text-left hover:bg-slate-50 flex items-center justify-between ${
                        selectedUser?.id === user.id ? "bg-primary-50 border-l-2 border-primary-500" : ""
                      }`}
                    >
                      <div>
                        <div className="font-medium text-slate-800">{user.name}</div>
                        <div className="text-sm text-slate-500">{user.email}</div>
                      </div>
                      {selectedUser?.id === user.id && (
                        <CheckCircle className="w-4 h-4 text-primary-600" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Role Selection */}
              <div>
                <Label className="text-slate-700">Select Role</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {Object.entries(ROLE_CONFIG).map(([role, config]) => {
                    const Icon = config.icon;
                    const isSelected = selectedRole === role;
                    
                    return (
                      <button
                        key={role}
                        onClick={() => setSelectedRole(role)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? `border-${config.color}-500 bg-${config.color}-50`
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${isSelected ? `text-${config.color}-600` : "text-slate-400"}`} />
                          <span className={`text-sm font-medium ${isSelected ? `text-${config.color}-700` : "text-slate-700"}`}>
                            {config.label}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Scope Selection */}
              <div>
                <Label className="text-slate-700">Scope</Label>
                <div className="flex gap-2 mt-2">
                  {["global", "committee", "meeting"].map((scope) => (
                    <button
                      key={scope}
                      onClick={() => setScopeType(scope)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium capitalize ${
                        scopeType === scope
                          ? "bg-primary-600 text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {scope}
                    </button>
                  ))}
                </div>

                {scopeType === "committee" && (
                  <Input
                    placeholder="Committee name..."
                    value={committeeScope}
                    onChange={(e) => setCommitteeScope(e.target.value)}
                    className="mt-2"
                  />
                )}

                {scopeType === "meeting" && (
                  <Input
                    placeholder="Meeting ID..."
                    value={meetingScope}
                    onChange={(e) => setMeetingScope(e.target.value)}
                    className="mt-2"
                  />
                )}
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAssignRole} 
                disabled={!selectedUser || saving}
                className="bg-primary-600 hover:bg-primary-700"
              >
                {saving ? "Assigning..." : "Assign Role"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
